import { isAfter } from 'date-fns';

import { TYPES_FIGURANT_ID } from 'commons/constants/figurants';
import { useUserData } from 'commons/contexts';
import { findDocument } from 'commons/utils/figurant';
import { DocumentType } from 'commons/constants/documents';

import { KeyTypeFigurant, StatusFigurant } from 'models';

import { KeySrcTypes, SRC_TYPES } from '../@types';
import { buildActionProps } from '../utils';
import { useStyle } from './useColors';
import * as CONSTANTS from '../constants';
import * as PROFILES from 'commons/constants/profiles';

type Props = {
  item: StatusFigurant;
  type: KeyTypeFigurant;
  selected?: boolean;
  handleSelectedFigurant?: (item: StatusFigurant) => void;
  handleAssociateChild?: (item: StatusFigurant) => void;
  areAllSelected?: boolean;
  src?: KeySrcTypes;
};

export const useFigurantCard = ({
  item,
  type,
  selected,
  handleAssociateChild,
  handleSelectedFigurant,
  areAllSelected,
  src,
}: Props) => {
  const { hasProfiles } = useUserData();

  const cardType = CONSTANTS.renderCardTypes[type];
  const renderInfo = CONSTANTS.mountDatas[type](item);

  const updated_at = new Date(item.updated);
  const date = new Date();
  const updated = isAfter(updated_at, date.setMonth(date.getMonth() - 6));

  const name = item?.name || item?.animalBreed || `${item?.type} - ${item?.category}`;

  const figurantUnavailable = !item.isActive || item.statusBook === 'UNAVAILABLE';
  const figurantClimbed = src === SRC_TYPES.CLIMBED;

  const showBlock = hasProfiles([PROFILES.GESTAO_FIGURACAO, PROFILES.TIME_COMFIGURA]) && !item.isActive;
  const { color, colorSelectedFigurant, colorUnavailableFigurant } = useStyle({
    selected,
    showBlock,
    updated,
  });

  const isFiscal = type === CONSTANTS?.typeFiscal;
  const isChildFigurant = type === CONSTANTS?.typeChildFigurant;

  const submitBySrc = {
    [SRC_TYPES.CLIMBED]: buildActionProps(
      handleSelectedFigurant as () => void,
      selected ? 'Cancelar Escalação' : `Escalar ${isChildFigurant ? 'Figurante' : TYPES_FIGURANT_ID[type]}`,
      (name?: string) => `${name} foi escalado(a) como ${isFiscal ? ' fiscal' : ' figurante'}!`,
      '28px'
    ),
    [SRC_TYPES.LICENSE]: buildActionProps(
      handleAssociateChild as () => void,
      selected ? 'Cancelar Associação' : 'Associar Criança',
      () => 'Figurante associado!',
      '42px'
    ),
    [SRC_TYPES.SEARCH]: {
      onClick: () => {},
      text: '',
      getDescription: () => '',
      fontSize: '',
    },
  };

  const isTextAvailable = !(figurantUnavailable && figurantClimbed && !selected);
  const figurantClimbedAndUnavailable = figurantClimbed && figurantUnavailable;
  const extraUnavailable = figurantClimbedAndUnavailable && type !== CONSTANTS?.typeFiscal;

  const personFigurant = type === CONSTANTS?.typeDefaultFigurant || isChildFigurant;
  const missingInformation = personFigurant && figurantClimbed && item.hasDataMissing && !extraUnavailable;
  const isAvailableToShowSelectButton = [SRC_TYPES.CLIMBED, SRC_TYPES.LICENSE];

  const isDisabled = missingInformation || figurantUnavailable || (areAllSelected && !selected);

  const variationStatusColor = selected ? colorSelectedFigurant : color;

  const statusColor = () => (missingInformation ? CONSTANTS?.ERROR_COLOR : variationStatusColor);
  const figurantCpf = findDocument(item?.documents, DocumentType.CPF)?.documentContent;

  return {
    submitBySrc,
    colorUnavailableFigurant,
    name,
    isTextAvailable,
    isAvailableToShowSelectButton,
    isDisabled,
    statusColor,
    figurantCpf,
    updated_at,
    showBlock,
    colorSelectedFigurant,
    figurantUnavailable,
    missingInformation,
    extraUnavailable,
    cardType,
    renderInfo,
  };
};
